import {useCallback} from 'react';

import {useQueryClient} from '@tanstack/react-query';
import {
  useCustomerBaskets,
  useCustomerId,
  useShopperBasketsMutation,
} from '@salesforce/commerce-sdk-react';
import {isServer} from '@salesforce/retail-react-app/app/utils/utils';

import {seedCartFlyoutDisplayFromLines} from '@amblique/king-living/app/utils/cart-flyout-display';
import {
  enqueueBasketMutation,
  getBasketFromCustomerBasketsCache,
} from '@amblique/king-living/app/utils/basket-mutation-queue';

/**
 * Resolves the active basket id from getCustomerBaskets payload.
 * Prefer the first basket with an id; do not rely only on `total` (can disagree with `baskets`).
 */
function pickBasketIdFromCustomerBaskets(data) {
  const list = data?.baskets;

  if (!Array.isArray(list) || list.length === 0) {
    return undefined;
  }
  const first = list.find((b) => b?.basketId);

  return first?.basketId;
}

/**
 * SFAPI may return an existing basket id when createBasket hits customer-baskets-quota-exceeded.
 */
async function parseQuotaExceededBasketId(error) {
  let payload;

  if (error?.response?.json && typeof error.response.json === 'function') {
    try {
      payload = await error.response.json();
    } catch {
      return undefined;
    }
  } else if (error?.body && typeof error.body === 'object') {
    payload = error.body;
  } else if (error?.data && typeof error.data === 'object') {
    payload = error.data;
  } else if (typeof error?.message === 'string') {
    try {
      payload = JSON.parse(error.message);
    } catch {
      return undefined;
    }
  }

  if (!payload) {
    return undefined;
  }

  const isQuota =
    payload.title === 'Customer Baskets Quota Exceeded' ||
    (typeof payload.type === 'string' &&
      payload.type.includes('customer-baskets-quota-exceeded'));

  if (!isQuota) {
    return undefined;
  }

  const raw = payload.basketIds;

  if (typeof raw === 'string' && raw.trim()) {
    return raw.trim();
  }

  if (Array.isArray(raw) && raw[0]) {
    return String(raw[0]);
  }

  return undefined;
}

/**
 * Adds line items to the shopper basket, creating a basket only when none exists.
 *
 * The SDK helper can call createBasket while React Query still has no basket row (stale cache
 * or timing after the first create). That produces SFCC "Customer Baskets Quota Exceeded" when
 * basket-per-customer limits are low. This hook refetches customer baskets once before creating.
 *
 * Basket id is re-resolved inside the mutation queue so a prior last-item `deleteBasket`
 * cannot leave ATC targeting a deleted id (DE-355).
 */
export function useAddItemToNewOrExistingBasket() {
  const queryClient = useQueryClient();
  const customerId = useCustomerId();
  const {data: basketsData, refetch: refetchCustomerBaskets} = useCustomerBaskets(
    {parameters: {customerId}},
    {enabled: !!customerId && !isServer}
  );
  const createBasket = useShopperBasketsMutation('createBasket');
  const addItemToBasket = useShopperBasketsMutation('addItemToBasket');

  const addAndSeedFlyoutDisplay = useCallback(
    async (productItems, preferredBasketId) => {
      seedCartFlyoutDisplayFromLines(queryClient, productItems);

      // KL-1029 / DE-355 — serialize resolve+add with remove/update. Prefer the
      // caller's basket id when it still exists; otherwise recover/create.
      const basket = await enqueueBasketMutation(async () => {
        let basketId;

        if (customerId) {
          basketId =
            getBasketFromCustomerBasketsCache(
              queryClient,
              preferredBasketId,
              customerId
            )?.basketId ??
            getBasketFromCustomerBasketsCache(queryClient, undefined, customerId)
              ?.basketId;
        }

        if (!basketId) {
          try {
            const created = await createBasket.mutateAsync({body: {}});

            basketId = created?.basketId;
          } catch (createError) {
            const reuseBasketId = await parseQuotaExceededBasketId(createError);

            if (!reuseBasketId) {
              throw createError;
            }

            basketId = reuseBasketId;
          }
        }

        if (!basketId) {
          throw new Error('Something is wrong. Please try again');
        }

        return addItemToBasket.mutateAsync({
          parameters: {basketId},
          body: productItems,
        });
      });

      seedCartFlyoutDisplayFromLines(queryClient, basket?.productItems);

      return basket;
    },
    [addItemToBasket, createBasket, customerId, queryClient]
  );

  const addItemToNewOrExistingBasket = useCallback(
    async (productItems) => {
      let preferredBasketId = pickBasketIdFromCustomerBaskets(basketsData);

      if (!preferredBasketId && customerId) {
        const {data: refreshed} = await refetchCustomerBaskets();

        preferredBasketId = pickBasketIdFromCustomerBaskets(refreshed);
      }

      return addAndSeedFlyoutDisplay(productItems, preferredBasketId);
    },
    [addAndSeedFlyoutDisplay, basketsData, customerId, refetchCustomerBaskets]
  );

  return {addItemToNewOrExistingBasket};
}
